import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { setClient as setClientAction } from '../actions/client';
import { selectIsLoggedIn } from '../reducers/client';
import PartenaireContext from '../contexts/PartenaireContext';
import * as api from '../utils/api';
import { resumeReparation } from '../utils/reparation';
import Link from './shared/atoms/Link';
import Box from './shared/atoms/Box';
import Input from './shared/atoms/Input';
import Button from './shared/atoms/Button';
import Alert from './shared/molecules/Alert';
import { analyticsIdentify } from '../utils/utils';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import useAlert from '../hooks/useModal';
// Components
import LoginForm from './connexionModule/LoginForm';
import ResetForm from './connexionModule/ResetForm';
import SignupForm from './connexionModule/SignupForm';

const SIGNUP = 'SIGNUP';
const LOGIN = 'LOGIN';
const RESET_PASSWORD = 'RESET_PASSWORD';
const RESET_PASSWORD_MAIL = 'RESET_PASSWORD_MAIL';

export const PLACEHOLDER_PHONE = '0000000000';

const withTimeout = (promise, timeoutMs) => {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error('Google login timeout'));
    }, timeoutMs);

    promise
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch((err) => {
        clearTimeout(timer);
        reject(err);
      });
  });
};

export const CompletePhoneContent = ({ client, onDone }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [tel, setTel] = useState(
    client && client.tel && client.tel !== PLACEHOLDER_PHONE ? client.tel : '',
  );
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!tel) {
      setError(t('required.information', 'Ce champ est obligatoire'));
      return;
    }

    setLoading(true);
    const response = await api.put(`/clients/${client.id}`, { tel });
    setLoading(false);

    if (response && response.id) {
      dispatch(setClientAction(response));
      if (onDone) {
        onDone();
      }
    } else if (response && response.errors && response.errors.tel) {
      const telErrors = Array.isArray(response.errors.tel)
        ? response.errors.tel.join(' ')
        : response.errors.tel;
      setError(telErrors);
    } else {
      setError(
        t(
          'googleauth.error.phoneUpdate',
          'Impossible de mettre à jour votre téléphone, veuillez réessayer.',
        ),
      );
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <Alert status="danger" mb={3}>
          {error}
        </Alert>
      )}
      <Input
        mb={{ xs: 2, lg: 4 }}
        color="black"
        bg="white"
        placeholder={t('phone')}
        label={t('phone.inputlabel')}
        value={tel}
        onChange={(e) => setTel(e.target.value)}
      />
      <Box flexDirection="row" justifyContent="flex-end" mt={4}>
        <Button
          type="submit"
          loading={loading}
          data-testid="google-phone-update-button">
          {t('update')}
        </Button>
      </Box>
    </form>
  );
};

const ConnexionModuleBase = ({
  partenaire,
  tracking,
  redir = false,
  loginFirst,
  titleSignup,
  ctaLabelSignup,
  titleSignin,
  ctaLabelSignin,
  initialType = loginFirst ? LOGIN : SIGNUP,
  noPadding = false,
  bg = 'gray.lightest',
  closeModal = () => {},
  /*
   * Le module est monté DANS une page qui doit rester à l'écran : on referme,
   * on ne navigue pas (FIXERLIGHT-020, le tunnel de la fiche annuaire).
   *
   * `redir === 'giftcard'` faisait déjà exactement ça, mais par un littéral :
   * l'intention « reste sur place » était portée par le nom d'une AUTRE
   * fonctionnalité. Un appelant tiers qui s'en servait héritait aussi de sa
   * destination Google. Cette option dit la chose au lieu de l'emprunter.
   */
  stayOnPage = false,
  /*
   * Inscription depuis la page « Créer mon espace » (client venu du site d'un
   * réparateur) : champs pré-remplis, champs ajoutés au corps de `POST /clients`
   * (le jeton de la demande), pas de case newsletter, Google à part.
   */
  signupInitialValues,
  signupExtra,
  withNewsletter = true,
  withGoogle = true,
  signupCompact = false,
  ctaSticky = false,
  sousBouton = null,
  /*
   * Appelé avec le compte créé, juste après l'inscription. Avec
   * `stayAfterSignup`, le module ne redirige pas : l'appelant montre lui-même
   * son écran de succès (« Créer mon espace », client venu du site d'un
   * réparateur).
   */
  onSignup,
  stayAfterSignup = false,
  /*
   * Email déjà connu : au lieu d'une erreur sous le champ, le module bascule
   * sur la connexion, email pré-rempli, avec ce message. `onLogin` reçoit le
   * compte connecté ; avec `stayAfterLogin` il ne redirige pas (l'appelant
   * rattache le rendez-vous, puis montre son écran).
   */
  existingAccountNotice = null,
  loginInitialEmail = '',
  onLogin,
  stayAfterLogin = false,
  // Dit à l'appelant si le module montre l'inscription ou la connexion.
  onTypeChange,
  ...props
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const store = useSelector((state) => state);
  const isLoggedIn = useSelector((state) => selectIsLoggedIn(state));
  const showAlert = useAlert();
  const history = useHistory();
  const [type, setType] = useState(initialType);

  useEffect(() => {
    if (onTypeChange) onTypeChange(type);
  }, [type]);
  const [loginemail, setLoginemail] = useState('');
  const [resetSend, setResetSend] = useState(false);
  const [email, setEmail] = useState();
  const product_id = useSelector((s) => s.course.product_id);
  const [isGoogleLoggingIn, setIsGoogleLoggingIn] = useState(false);
  const [googleError, setGoogleError] = useState(null);
  const signupSansRedirection = useRef(false);
  const [emailConnu, setEmailConnu] = useState(loginInitialEmail);
  const [noticeConnexion, setNoticeConnexion] = useState(null);

  useEffect(() => {
    if (signupSansRedirection.current) return;
    const hasValidPhone =
      store &&
      store.client &&
      store.client.tel &&
      store.client.tel !== PLACEHOLDER_PHONE;

    if (isLoggedIn && hasValidPhone && type !== RESET_PASSWORD_MAIL) {
      // `stayOnPage` passe AVANT `redir` : il était testé à l'intérieur de
      // `if (redir)`, et un appelant qui ne donne pas de destination (le tunnel
      // de la fiche annuaire) partait sur /account, sa demande perdue.
      if (stayOnPage) {
        closeModal();
      } else if (redir) {
        if (redir === 'reparation') {
          resumeReparation(store, history);
        } else if (redir === 'giftcard') {
          closeModal();
        } else {
          history.push(redir);
        }
      } else {
        history.push('/account');
      }
    } else {
      console.log('not logged in');
    }
  }, [isLoggedIn]);

  const handleGoogleLogin = () => {
    if (typeof window === 'undefined') return;

    setIsGoogleLoggingIn(true);

    const apiBaseUrl = getApiBaseUrl();

    const url = new URL('/auth/google/redirect', apiBaseUrl);

    /*
     * Google sort du site : c'est un aller-retour pleine page, et `redir` dit
     * où revenir. Un appelant qui ne veut pas bouger n'a pas de destination à
     * donner — il veut revenir ICI. Sans ça, un client qui se connecte par
     * Google depuis une fiche d'annuaire atterrissait sur la page des cartes
     * cadeaux, parce que c'est le nom que portait « reste sur place ».
     */
    const destination = stayOnPage
      ? `${window.location.pathname}${window.location.search}`
      : redir;

    if (destination) {
      url.searchParams.set('redir', destination);
    }

    window.location.href = url.toString();
  };

  const doLogin = async (values, { setSubmitting, setErrors }) => {
    const rep = await api.post('/clients/sessions', {
      email: values.email,
      password: values.password,
    });
    setEmail(values.email);
    setSubmitting(false);
    if (rep.error_message) setErrors({ password: rep.error_message });
    else if (!!rep.id && rep.token) {
      // Posé AVANT le dispatch : c'est lui qui déclenche la redirection.
      if (stayAfterLogin) signupSansRedirection.current = true;
      dispatch(setClientAction(rep));
      analyticsIdentify(rep.id, {
        email: rep.email,
        firstName: rep.prenom,
        lastName: rep.nom,
        phone: rep.tel,
      });
      if (onLogin) onLogin(rep);
    }
  };

  const doReset = async (values, { setSubmitting, setErrors }) => {
    const resetObject = { email: values.email, type: 'RESET_PASSWORD' };
    if (redir) resetObject.redir = redir;
    await api.post('/auth_tokens', resetObject);
    setSubmitting(false);
    setResetSend(true);
    setType(LOGIN);
  };

  const _switch = (type) => {
    setType(type);
  };

  const doSignup = async (values, { setSubmitting, setErrors }) => {
    // if (values.password !== values.password_confirm) {
    //   setSubmitting(false);
    //   setErrors({
    //     password_confirm: 'La confirmation ne correspond pas au mot de passe.',
    //   });
    //   return;
    // }
    if (partenaire) {
      values.acquisition_type = partenaire.toUpperCase();
    }
    if (tracking) {
      values.acquisition_type = tracking.toUpperCase();
    }

    const rep = await api.post('/clients', { ...values, ...signupExtra });

    setSubmitting(false);

    if (rep.id) {
      // Posé AVANT le dispatch : c'est lui qui déclenche la redirection.
      if (stayAfterSignup) signupSansRedirection.current = true;
      dispatch(setClientAction(rep));
      analyticsIdentify(rep.id, {
        email: rep.email,
        firstName: rep.prenom,
        lastName: rep.nom,
        phone: rep.tel,
      });
      if (onSignup) onSignup(rep);
    } else if (
      rep.errors &&
      existingAccountNotice &&
      rep.errors.email_already_in_use
    ) {
      setEmailConnu(values.email);
      setNoticeConnexion(existingAccountNotice);
      setType(LOGIN);
    } else if (rep.errors) {
      const serializedErrors = {};
      for (const k in rep.errors) {
        serializedErrors[k] = rep.errors[k].reduce((i, b) => b + i, '');
      }

      if (
        typeof rep.errors.email_already_in_use !== 'undefined' &&
        !!rep.errors.email_already_in_use
      )
        serializedErrors.email = (
          <span>
            {t('connexionmodule.text')}&nbsp;
            <Trans>
              <Link
                color="primary.darkest"
                fontWeight="bold"
                onClick={() => _switch(LOGIN)}
                style={{ borderBottom: '1px solid #8F1505' }}>
                Cliquez ici
              </Link>
              &nbsp;pour vous connecter !
            </Trans>
          </span>
        );

      setErrors(serializedErrors);
    }
  };

  return (
    <div>
      {type === SIGNUP && (
        <SignupForm
          submit={doSignup}
          _switch={_switch}
          product_id={product_id}
          withTitle={titleSignup}
          ctaLabel={ctaLabelSignup}
          bg={bg}
          noPadding={noPadding}
          initialValues={signupInitialValues}
          withNewsletter={withNewsletter}
          withGoogle={withGoogle}
          compact={signupCompact}
          ctaSticky={ctaSticky}
          sousBouton={sousBouton}
          isGoogleLoggingIn={isGoogleLoggingIn}
          onGoogleClick={handleGoogleLogin}
          googleError={googleError}
        />
      )}
      {type === LOGIN && (
        <LoginForm
          SIGNUP={SIGNUP}
          RESET_PASSWORD={RESET_PASSWORD}
          resetSend={resetSend}
          submit={doLogin}
          _switch={_switch}
          withTitle={titleSignin}
          ctaLabel={ctaLabelSignin}
          withGoogle={withGoogle}
          initialEmail={emailConnu}
          notice={noticeConnexion}
          onChange={(e) => {
            setLoginemail({ loginemail: e.target.value });
          }}
          bg={bg}
          noPadding={noPadding}
          isGoogleLoggingIn={isGoogleLoggingIn}
          onGoogleClick={handleGoogleLogin}
          googleError={googleError}
        />
      )}
      {type === RESET_PASSWORD && (
        <ResetForm
          LOGIN={LOGIN}
          email={email}
          submit={doReset}
          _switch={_switch}
          defaultValue={loginemail}
          bg={bg}
          noPadding={noPadding}
        />
      )}
    </div>
  );
};

const ConnexionModule = (props) => (
  <PartenaireContext.Consumer>
    {({ partenaire, tracking }) => (
      <ConnexionModuleBase
        partenaire={partenaire}
        tracking={tracking}
        {...props}
      />
    )}
  </PartenaireContext.Consumer>
);

export default ConnexionModule;
